import useStatusMutation from "hook/useStatusMutation";
import React, { forwardRef } from "react";
import { BigButton } from "./ButtonStyled";
import { useMutation } from "react-query";
import { StyledComponent } from "styled-components";

export interface MutationButtonProps {
  onClick?: (mutation: ReturnType<typeof useMutation>["mutate"]) => void;
  mutationFn: (payload: any) => Promise<any>;
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
  ButtonComponent?: StyledComponent<"button", any, any>;
  disabled?: boolean;
}

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClick?: (mutation: ReturnType<typeof useMutation>["mutate"]) => void;
  mutationFn: (payload: any) => Promise<any>;
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
  ButtonComponent?: StyledComponent<"button", any, any>;
}

export interface UseMutationButton {
  mutation: ReturnType<typeof useMutation>;
  handleClick: () => void;
  MutationButton: React.ForwardRefExoticComponent<
    React.ButtonHTMLAttributes<HTMLButtonElement> &
      React.RefAttributes<HTMLButtonElement>
  >;
}

function useMutationButton({
  onClick,
  mutationFn,
  onSuccess,
  onError,
  disabled,
  ButtonComponent = BigButton,
  ...rest
}: Props): UseMutationButton {
  const mutation = useStatusMutation(mutationFn, {
    onSuccess,
    onError,
  });
  const handleClick = async () => {
    if (!mutation.idle) return;
    if (onClick) {
      onClick(mutation);
    } else {
      mutation.mutate();
    }
  };

  const MutationButton = forwardRef(
    (
      rest: React.ButtonHTMLAttributes<HTMLButtonElement>,
      ref: React.Ref<HTMLButtonElement>
    ) => {
      return (
        <ButtonComponent
          ref={ref}
          onClick={handleClick}
          disabled={!mutation.idle || disabled}
          data-testid={`${mutationFn.name ?? "unknown"}-mutation-button-click`}
          {...rest}
        />
      );
    }
  );
  return {
    mutation,
    MutationButton,
    handleClick,
  };
}
export default useMutationButton;
